// Display helpers — compact USD, addresses, prices, amounts. Two things are deliberately NOT here:
//
//   * `ago()` / `age()` live in `lib/time.ts`, because they need a NOW and this app's now is chain time
//     (`asOf.second`), never wall clock — see that file.
//   * raw→ui conversion is `uiFromRaw()` below and is exact (BigInt, string space). `Number(raw) / 10 **
//     decimals` silently loses precision above 2^53 raw units, and a display helper is where that creeps in.
//
// Everything here is PURE and formats for the eye. Nothing here may be used to size an order: `trade/amounts.ts`
// owns `toRaw`/`fromRaw`/`pctOfRaw` and `tests/structure.test.ts` keeps that converter inside `src/trade/**`.

// Subscript-zero notation for tiny decimals so they don't collapse to "0" and lose information:
// 0.000001234 → "0.0₅1234" (the ₅ = five leading zeros after the decimal). `sig` significant digits kept.
// Assumes 0 < v < 1. Below 4 leading zeros it's plain (trimmed) precision — subscripts only when they help.
const SUBSCRIPTS = "₀₁₂₃₄₅₆₇₈₉";
const toSub = (n: number): string =>
  String(n)
    .split("")
    .map((d) => SUBSCRIPTS[Number(d)])
    .join("");

function subDecimal(v: number, sig = 4): string {
  const m = /^0\.(0*)(\d+)/.exec(v.toFixed(20));
  if (!m) return v.toPrecision(sig);
  const zeros = (m[1] ?? "").length;
  const digits = (m[2] ?? "").replace(/0+$/, "").slice(0, sig) || "0";
  if (zeros < 4)
    return v
      .toPrecision(sig)
      .replace(/(\.\d*?)0+$/, "$1")
      .replace(/\.$/, "");
  return `0.0${toSub(zeros)}${digits}`;
}

export function usd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  if (a >= 1) return `$${n.toFixed(2)}`;
  if (a === 0) return "$0";
  return `${n < 0 ? "-$" : "$"}${subDecimal(a, 3)}`;
}

export function price(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n >= 1) return `$${n.toFixed(4)}`;
  if (n <= 0) return "$0";
  return `$${subDecimal(n, 4)}`;
}

export function num(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  if (a > 0 && a < 0.01) return `${n < 0 ? "-" : ""}${subDecimal(a, 4)}`; // tiny → subscript, don't round to 0
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export const shortAddr = (a: string | null | undefined, n = 4): string =>
  !a ? "—" : `${a.slice(0, n)}…${a.slice(-n)}`;

export const pct = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(1)}%`;

/**
 * Signed percentage for a price change (e.g. "+5.2%" / "-3.1%").
 *
 * ⚠️ IT TAKES A RATIO AND SCALES IT. `WindowStats.priceChangePct` is `(live - base) / base` — see
 * `StoreFacade.changeVs` — so it is a FRACTION despite the `Pct` in its name, and it is the only thing this
 * function is ever passed.
 *
 * THE MISSING `x 100` MADE EVERY CHANGE ON THE SITE READ "+0.0%". `(-0.006323).toFixed(1)` is `"-0.0"`, and
 * a ratio has to exceed 0.05 before the first decimal moves at all — so SOL at -0.63% over 24 h, and a
 * launch at +3.87%, both rendered as a signed zero on the board, the launch board and the token header at
 * once. It looked like the windows had stopped being computed; they never had. Reported 2026-08-18.
 *
 * IT IS SCALED HERE RATHER THAN AT THE THREE CALL SITES deliberately, and that is the opposite of the
 * `bondingProgress * 100` / `coverage * 100` convention beside it: those feed the GENERAL `pct`, which
 * formats a number that is already a percentage and has many kinds of caller. This function has exactly one
 * kind, so putting the conversion in it makes the unit impossible to get wrong at a fourth call site.
 */
export const chgPct = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) ? "—" : `${n >= 0 ? "+" : ""}${(n * 100).toFixed(1)}%`;

// A trade's side normalized to buy/sell (the backend uses b/s or buy/sell).
export const isBuy = (side: string): boolean => side === "b" || side === "buy";

/** EXACT raw base units → a decimal UI string, in string space with `BigInt`. No float, at any width.
 *
 *  THE ONE raw→ui conversion in the app. `trade/amounts.ts` (`fromRaw`) must call this rather than write a
 *  second one: a balance can exceed 2^53 raw units, and two conversions that disagree on the last digit are a
 *  "sell 100%" that either leaves dust behind or asks for more than you hold — and the second one fails AFTER a
 *  signature. Trailing zeros are trimmed; the integer part is never truncated. */
export function uiFromRaw(raw: string, decimals: number): string {
  if (!/^-?\d+$/.test(raw)) return "—";
  const negative = raw.startsWith("-");
  const digits = (negative ? raw.slice(1) : raw).replace(/^0+(?=\d)/, "");
  if (decimals <= 0) return `${negative ? "-" : ""}${digits}`;
  const padded = digits.padStart(decimals + 1, "0");
  const whole = padded.slice(0, padded.length - decimals);
  const fraction = padded.slice(padded.length - decimals).replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

/** A raw amount, compacted for display ("23,322", "1.2M", "0.0₅1234").
 *
 *  The float appears ONLY here and only to choose a magnitude bucket for the eye. The exact value is
 *  `uiFromRaw` above, and an ORDER is sized from `trade/amounts.ts`, never from this string. */
export function amt(raw: string, decimals: number): string {
  const exact = uiFromRaw(raw, decimals);
  if (exact === "—") return "—";
  return num(Number(exact));
}
